import React, { useCallback, useEffect, useMemo, useState } from "react";
import { alpha } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import SkillAgentMoodboardDetail, {
  MoodboardAssetBlobContext,
} from "../Skills/SkillAgentMoodboardDetail";
import {
  addMuestrasToMoodboardSession,
  createMuestrasMoodboard,
  createMuestrasMoodboardApi,
  deleteMuestrasMoodboard,
  fetchMuestrasMoodboardAssetBlobUrl,
} from "../../api/Muestras/muestrasMoodboardApi";
import { getFileContent } from "../../api/Design/fileApi";
import { fetchMuestraFilesCached } from "../../utils/muestraFilesRequest";
import { pickFilesForMuestraExportPhoto } from "../../utils/muestraFileCategories";

function muestraLabel(m) {
  return String(
    m?.NOMBRE_DISENO || m?.NombreCliente || m?.MUESTRA || m?.id || "",
  ).trim();
}

function muestraIdOf(m) {
  return Number(m?.id ?? m?.ID_MUESTRA) || 0;
}

const moodboardDialogBackdropSx = {
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  backgroundColor: "rgba(15, 23, 42, 0.45)",
};

function MuestraPickThumb({ muestra, already, checked, onToggle }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState("");
  const id = muestraIdOf(muestra);
  const label = muestraLabel(muestra) || `#${id}`;

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        const files = await fetchMuestraFilesCached(id);
        const pick = pickFilesForMuestraExportPhoto(files || [])[0];
        if (!pick?.path) return;
        const response = await getFileContent(pick.path);
        const { mimeType, data } = response?.data || {};
        if (!data || !String(mimeType || "").includes("image")) return;
        objectUrl = `data:${mimeType};base64,${data}`;
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) setSrc("");
      }
    })();
    return () => {
      revoked = true;
    };
  }, [id]);

  return (
    <Box
      onClick={() => {
        if (already) return;
        onToggle?.(!checked);
      }}
      title={
        already
          ? t("Samples.moodboardAlreadyAdded", {
              defaultValue: "Ya está en este moodboard",
            })
          : checked
            ? t("skillAgent.moodboard.unmarkSelection", {
                defaultValue: "Quitar selección",
              })
            : t("skillAgent.moodboard.markToAdd", {
                defaultValue: "Marcar para añadir",
              })
      }
      sx={{
        position: "relative",
        borderRadius: 1.5,
        overflow: "hidden",
        border: "2px solid",
        borderColor: checked
          ? "primary.main"
          : already
            ? "transparent"
            : "divider",
        cursor: already ? "default" : "pointer",
        bgcolor: "#eee",
        width: "100%",
        aspectRatio: "3 / 4",
        opacity: already ? 0.45 : 1,
        boxShadow: checked ? `0 0 0 1px ${alpha("#0ea5e9", 0.35)}` : "none",
      }}
    >
      {src ? (
        <Box
          component="img"
          src={src}
          alt={label}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            objectPosition: "center top",
            display: "block",
            filter: checked ? "brightness(0.82)" : "none",
          }}
        />
      ) : (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
          }}
        >
          <CircularProgress size={18} />
        </Box>
      )}
      {checked ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            bgcolor: alpha("#0ea5e9", 0.18),
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
      ) : null}
      {!already ? (
        <Checkbox
          size="small"
          checked={checked}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onToggle?.(e.target.checked)}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.25,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      ) : (
        <Chip
          size="small"
          label={t("Samples.moodboardOnBoardChip", {
            defaultValue: "En board",
          })}
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 2,
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 700,
            bgcolor: alpha("#fff", 0.92),
          }}
        />
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
          px: 0.75,
          py: 0.4,
          fontSize: "0.68rem",
          fontWeight: 700,
          color: "#fff",
          bgcolor: "rgba(0,0,0,0.55)",
          lineHeight: 1.25,
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

export default function MuestrasMoodboardDialog({
  open,
  onClose,
  muestras = [],
  initialMuestraIds = [],
}) {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState(null);
  const [bootError, setBootError] = useState("");
  const [booting, setBooting] = useState(false);
  const [needsPdfExport, setNeedsPdfExport] = useState(true);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addPick, setAddPick] = useState(() => new Set());
  const [addQuery, setAddQuery] = useState("");
  const [placeFilter, setPlaceFilter] = useState("all");
  const [showCount, setShowCount] = useState(36);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [usedMuestraIds, setUsedMuestraIds] = useState(() => new Set());
  const [boardKey, setBoardKey] = useState(0);

  const openAddMuestras = useCallback(() => {
    setAddError("");
    setAddPick(new Set());
    setAddQuery("");
    setPlaceFilter("all");
    setShowCount(36);
    setAddOpen(true);
  }, []);

  const boardApi = useMemo(
    () => ({
      ...createMuestrasMoodboardApi(),
      openAddMuestras,
      addMuestrasLabel: t("skillAgent.moodboard.addFromMuestras", {
        defaultValue: "Añadir fotos de muestras",
      }),
    }),
    [openAddMuestras, t],
  );

  const fetchBlob = useMemo(() => {
    if (!sessionId) return async () => "";
    return (assetId) => fetchMuestrasMoodboardAssetBlobUrl(sessionId, assetId);
  }, [sessionId]);

  const initialIdsKey = useMemo(
    () =>
      (initialMuestraIds || [])
        .map((n) => Number(n))
        .filter((n) => n > 0)
        .sort((a, b) => a - b)
        .join(","),
    [initialMuestraIds],
  );

  useEffect(() => {
    if (!open) {
      setSessionId(null);
      setBootError("");
      setBooting(false);
      setNeedsPdfExport(true);
      setConfirmCloseOpen(false);
      setAddOpen(false);
      setAddPick(new Set());
      setAddQuery("");
      setAddError("");
      setUsedMuestraIds(new Set());
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setBooting(true);
      setBootError("");
      try {
        const ids = initialIdsKey
          ? initialIdsKey.split(",").map((n) => Number(n))
          : [];
        const created = await createMuestrasMoodboard(ids);
        if (cancelled) {
          await deleteMuestrasMoodboard(created.id);
          return;
        }
        setSessionId(created.id);
        setUsedMuestraIds(
          new Set(
            (created.muestraIds || ids)
              .map((n) => Number(n))
              .filter((n) => n > 0),
          ),
        );
        setNeedsPdfExport(true);
        setBoardKey((k) => k + 1);
      } catch (err) {
        if (!cancelled) {
          setBootError(
            err?.response?.data?.error ||
              t("Samples.moodboardCreateError", {
                defaultValue: "No se pudo crear el moodboard",
              }),
          );
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, initialIdsKey, t]);

  const handleCloseGuardChange = useCallback(({ needsPdfExport: needs }) => {
    setNeedsPdfExport(Boolean(needs));
  }, []);

  const finishClose = useCallback(async () => {
    const id = sessionId;
    setSessionId(null);
    onClose?.();
    if (id) await deleteMuestrasMoodboard(id);
  }, [onClose, sessionId]);

  const requestClose = useCallback(() => {
    if (sessionId && needsPdfExport) {
      setConfirmCloseOpen(true);
      return;
    }
    finishClose();
  }, [finishClose, needsPdfExport, sessionId]);

  const filteredMuestras = useMemo(() => {
    const q = addQuery.trim().toLowerCase();
    return (Array.isArray(muestras) ? muestras : []).filter((m) => {
      const id = muestraIdOf(m);
      if (!id) return false;
      const already = usedMuestraIds.has(id);
      if (placeFilter === "chosen" && !already) return false;
      if (placeFilter === "notChosen" && already) return false;
      if (!q) return true;
      const hay = [
        muestraLabel(m),
        m?.MARCA,
        m?.CLIENTE,
        m?.CustomerName,
        m?.TEMPORADA,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [muestras, addQuery, usedMuestraIds, placeFilter]);

  const handleAddMuestras = async () => {
    if (!sessionId || !addPick.size) return;
    setAdding(true);
    setAddError("");
    try {
      const ids = [...addPick];
      const data = await addMuestrasToMoodboardSession(sessionId, ids);
      setUsedMuestraIds((prev) => {
        const next = new Set(prev);
        for (const id of data?.muestraIds || ids) next.add(Number(id));
        return next;
      });
      setAddOpen(false);
      setAddPick(new Set());
      setNeedsPdfExport(true);
      setBoardKey((k) => k + 1);
    } catch (err) {
      setAddError(
        err?.response?.data?.error ||
          t("Samples.moodboardAddError", {
            defaultValue: "No se pudieron añadir las muestras",
          }),
      );
    } finally {
      setAdding(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        onClose={requestClose}
        PaperProps={{
          sx: {
            bgcolor: "background.default",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            {t("Samples.moodboardTitle", {
              defaultValue: "Moodboard de muestras",
            })}
          </Typography>
          <IconButton aria-label={t("Samples.cancel")} onClick={requestClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: "flex" }}>
          {booting ? (
            <Box sx={{ flex: 1, display: "grid", placeItems: "center" }}>
              <CircularProgress size={32} />
            </Box>
          ) : bootError ? (
            <Box sx={{ flex: 1, p: 3 }}>
              <Alert severity="error">{bootError}</Alert>
              <Button
                sx={{ mt: 2, textTransform: "none", fontWeight: 700 }}
                onClick={finishClose}
              >
                {t("Samples.cancel")}
              </Button>
            </Box>
          ) : sessionId ? (
            <MoodboardAssetBlobContext.Provider value={fetchBlob}>
              <SkillAgentMoodboardDetail
                key={`${sessionId}-${boardKey}`}
                moodboardId={sessionId}
                boardApi={boardApi}
                onCloseGuardChange={handleCloseGuardChange}
              />
            </MoodboardAssetBlobContext.Provider>
          ) : null}
        </Box>
      </Dialog>

      <Dialog
        open={addOpen}
        onClose={() => !adding && setAddOpen(false)}
        maxWidth="lg"
        fullWidth
        slotProps={{ backdrop: { sx: moodboardDialogBackdropSx } }}
        BackdropProps={{ sx: moodboardDialogBackdropSx }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "92vh",
            minHeight: { md: "78vh" },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.25,
            pt: 2,
            flexShrink: 0,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
          >
            <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
              <Typography variant="h6" fontWeight={800} component="div">
                {t("skillAgent.moodboard.addFromMuestras", {
                  defaultValue: "Añadir fotos de muestras",
                })}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {t("Samples.moodboardAddHint", {
                  defaultValue:
                    "Marca muestras para meter sus fotos (Presentacion + técnicas) en el board.",
                })}
              </Typography>
            </Box>
            <Stack
              spacing={0.75}
              sx={{
                flex: "0 1 auto",
                alignItems: { xs: "flex-start", sm: "flex-end" },
                ml: { sm: "auto" },
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                useFlexGap
                flexWrap="wrap"
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
              >
                {[
                  {
                    id: "all",
                    label: t("skillAgent.moodboard.dialog.filterAll", {
                      defaultValue: "Todas",
                    }),
                  },
                  {
                    id: "chosen",
                    label: t("skillAgent.moodboard.dialog.filterChosen", {
                      defaultValue: "Elegido",
                    }),
                  },
                  {
                    id: "notChosen",
                    label: t("skillAgent.moodboard.dialog.filterNotChosen", {
                      defaultValue: "No elegido",
                    }),
                  },
                ].map((opt) => (
                  <Chip
                    key={opt.id}
                    size="small"
                    label={opt.label}
                    color={placeFilter === opt.id ? "primary" : "default"}
                    variant={placeFilter === opt.id ? "filled" : "outlined"}
                    onClick={() => {
                      setPlaceFilter(opt.id);
                      setShowCount(36);
                    }}
                    sx={{ fontWeight: 700 }}
                  />
                ))}
              </Stack>
              <TextField
                size="small"
                value={addQuery}
                onChange={(e) => {
                  setAddQuery(e.target.value);
                  setShowCount(36);
                }}
                placeholder={t("Samples.search")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 260 } }}
              />
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1.5 }}>
          {addError ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {addError}
            </Alert>
          ) : null}
          {!filteredMuestras.length ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography fontWeight={700} color="text.secondary">
                {placeFilter === "chosen"
                  ? t("Samples.moodboardAddEmptyChosen", {
                      defaultValue: "Ninguna de estas está ya en el board",
                    })
                  : placeFilter === "notChosen"
                    ? t("Samples.moodboardAddEmpty", {
                        defaultValue: "No hay más muestras para añadir.",
                      })
                    : t("Samples.moodboardAddEmpty", {
                        defaultValue: "No hay más muestras para añadir.",
                      })}
              </Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  },
                  gap: 1.25,
                }}
              >
                {filteredMuestras.slice(0, showCount).map((m) => {
                  const id = muestraIdOf(m);
                  const already = usedMuestraIds.has(id);
                  return (
                    <MuestraPickThumb
                      key={id}
                      muestra={m}
                      already={already}
                      checked={!already && addPick.has(id)}
                      onToggle={(on) => {
                        setAddPick((prev) => {
                          const next = new Set(prev);
                          if (on) next.add(id);
                          else next.delete(id);
                          return next;
                        });
                      }}
                    />
                  );
                })}
              </Box>
              {filteredMuestras.length > showCount ? (
                <Button
                  size="small"
                  sx={{ mt: 1.5, textTransform: "none" }}
                  onClick={() => setShowCount((n) => n + 36)}
                >
                  {t("skillAgent.moodboard.dialog.loadMore", {
                    defaultValue: "Cargar más ({{n}})",
                    n: filteredMuestras.length - showCount,
                  })}
                </Button>
              ) : null}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
          <Button
            disabled={adding}
            onClick={() => setAddOpen(false)}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("Samples.cancel")}
          </Button>
          <Button
            variant="contained"
            disabled={adding || addPick.size === 0}
            onClick={handleAddMuestras}
            startIcon={
              adding ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{ textTransform: "none", fontWeight: 800 }}
          >
            {t("Samples.moodboardAddN", {
              defaultValue: "Añadir {{n}}",
              n: addPick.size,
            })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmCloseOpen}
        onClose={() => setConfirmCloseOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: "flex",
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: 2.5,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: (th) => alpha(th.palette.warning.main, 0.12),
              border: "1px solid",
              borderColor: (th) => alpha(th.palette.warning.main, 0.28),
              color: "warning.dark",
            }}
            aria-hidden
          >
            <PictureAsPdfOutlinedIcon sx={{ fontSize: 36 }} />
          </Box>
          <Box sx={{ minWidth: 0, pt: 0.25, flex: 1 }}>
            <Typography
              variant="h6"
              fontWeight={800}
              sx={{ lineHeight: 1.25, mb: 0.75 }}
            >
              {t("Samples.moodboardCloseTitle", {
                defaultValue: "¿Cerrar sin PDF?",
              })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("Samples.moodboardCloseBody", {
                defaultValue:
                  "Este moodboard no se guarda. Si no descargas el PDF, se pierde el trabajo.",
              })}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            px: 3,
            pb: 2.5,
            pt: 1.5,
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              setConfirmCloseOpen(false);
              finishClose();
            }}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("Samples.moodboardCloseWithoutPdf", {
              defaultValue: "Cerrar sin PDF",
            })}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setConfirmCloseOpen(false)}
            sx={{ textTransform: "none", fontWeight: 800 }}
            autoFocus
          >
            {t("Samples.moodboardKeepEditing", {
              defaultValue: "Seguir y exportar",
            })}
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
